import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FaArrowLeft, FaSpinner, FaTruck, FaMapMarkerAlt, FaCalendarAlt, FaCheckCircle,
  FaClock, FaUser, FaIdCard, FaExternalLinkAlt, FaFileAlt, FaImage, FaPhone
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import StoredRouteMap from '../components/StoredRouteMap';
import LoadDocumentsPanel from '../components/LoadDocumentsPanel';

// ============================================
// MY LOAD — the client's single-load detail page.
// Opens from the View button on a load (My Jobs / My Loads). Shows progress, the truck /
// trailer / driver and whether their papers are still valid, and the tracking link.
// Deliberately NO prices beyond the client's own rate, and NEVER the subcontractor or
// what we pay them (the API strips those server-side).
// ============================================

const STATUS_META = {
  pending: { label: 'Pending', color: '#b45309', bg: '#fffbeb' },
  on_route_to_loading: { label: 'To Loading', color: '#7c3aed', bg: '#f5f3ff' },
  onsite: { label: 'On Site', color: '#2563eb', bg: '#eff6ff' },
  loading: { label: 'Loading', color: '#2563eb', bg: '#eff6ff' },
  en_route: { label: 'En Route', color: '#2563eb', bg: '#eff6ff' },
  at_destination: { label: 'At Destination', color: '#ea580c', bg: '#fff7ed' },
  delivered: { label: 'Delivered', color: '#16a34a', bg: '#f0fdf4' },
  completed: { label: 'Completed', color: '#16a34a', bg: '#f0fdf4' }
};

const STAGES = ['pending', 'on_route_to_loading', 'onsite', 'loading', 'en_route', 'at_destination', 'delivered', 'completed'];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// 🟢 Valid · 🟠 expires within 30 days · 🔴 expired.
// Same rule the staff-side compliance checker uses (routes/compliance.js).
function ExpiryChip({ date, label }) {
  if (!date) return null;
  const today = new Date(new Date().toDateString());
  const days = Math.round((new Date(date) - today) / 86400000);
  const tone = days < 0
    ? { color: '#b91c1c', backgroundColor: '#fef2f2' }
    : days <= 30
      ? { color: '#b45309', backgroundColor: '#fffbeb' }
      : { color: '#15803d', backgroundColor: '#f0fdf4' };
  const text = days < 0 ? `Expired ${Math.abs(days)}d ago` : days <= 30 ? `Expires in ${days}d` : 'Valid';
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={tone}>
      {label ? `${label}: ` : ''}{text}
    </span>
  );
}

function ClientLoadDetail({ onLogout }) {
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [load, setLoad] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchLoad = async () => {
      try {
        const r = await fetch(`/client/loads/${id}`);
        const d = await r.json();
        if (cancelled) return;
        if (!d.success) { setError(d.error || 'This load could not be found.'); return; }
        setLoad(d.load);
      } catch (err) {
        if (!cancelled) setError('Could not load this load. Please refresh and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLoad();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <ClientLayout activeTab="loads" onLogout={onLogout} user={user}>
        <div className="py-16 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={24} /></div>
      </ClientLayout>
    );
  }

  if (error || !load) {
    return (
      <ClientLayout activeTab="loads" onLogout={onLogout} user={user}>
        <Link to="/client-loads" className="mb-4 inline-flex items-center gap-2 text-gray-500 hover:text-navy">
          <FaArrowLeft /> All loads
        </Link>
        <div className="bg-white rounded-xl border p-10 text-center" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-5xl mb-3">🚚</div>
          <p className="text-sm text-gray-500">{error || 'This load could not be found.'}</p>
        </div>
      </ClientLayout>
    );
  }

  const job = load.job || {};
  const status = String(load.status || '').toLowerCase();
  const meta = STATUS_META[status] || { label: load.status || '—', color: '#64748b', bg: '#f1f5f9' };
  const idx = Math.max(STAGES.indexOf(status), 0);
  const pct = Math.round((idx / (STAGES.length - 1)) * 100);
  const trackingHref = load.tracking_link
    ? (String(load.tracking_link).startsWith('http') ? load.tracking_link : `https://${load.tracking_link}`)
    : null;
  const stages = [
    { label: 'Scheduled', at: load.scheduled_pickup_at || load.scheduled_date || load.created_at },
    { label: 'Departed', at: load.actual_departure_at },
    { label: 'Arrived', at: load.actual_arrival_at },
    { label: 'Offloaded', at: load.actual_offload_at }
  ].filter((s) => s.at);

  return (
    <ClientLayout activeTab="loads" onLogout={onLogout} user={user}>
      <Link to="/client-loads" className="mb-4 inline-flex items-center gap-2 text-gray-500 hover:text-navy">
        <FaArrowLeft /> All loads
      </Link>

      {/* ── Header + progress ── */}
      <div className="bg-white rounded-2xl border p-6 mb-4" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs text-gray-400 font-medium">Load</p>
            <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>{load.load_number || '—'}</h1>
            {load.leg_label && <p className="text-xs text-gray-400 mt-0.5">{load.leg_label}</p>}
            {/* What this load is carrying (the line item on the job), so two loads on one job are
                never confused for each other. */}
            {(load.line_item?.title || load.line_item?.description || load.line_item?.commodity) && (
              <p className="text-sm font-medium mt-1" style={{ color: '#0A1929' }}>
                {load.line_item.title || load.line_item.description || load.line_item.commodity}
              </p>
            )}
            {load.scheduled_pickup_at && (
              <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                <FaCalendarAlt size={10} /> Scheduled {fmtDateTime(load.scheduled_pickup_at)}
              </p>
            )}
            {/* The job's own description - kept to the full detail page, not the load card. */}
            {load.job?.commodity && (
              <p className="text-xs mt-2 max-w-2xl whitespace-pre-line" style={{ color: '#4A5568' }}>{load.job.commodity}</p>
            )}
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
        </div>

        <div className="mt-6">
          <div className="relative h-2 rounded-full bg-gray-100">
            <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct}%`, backgroundColor: '#00A86B' }} />
            <span className="absolute -top-3 text-base" style={{ left: `calc(${pct}% - 10px)` }}>🚛</span>
          </div>
          <div className="flex justify-between text-[10px] mt-2" style={{ color: '#94A3B8' }}>
            <span>Pickup</span><span>In transit</span><span>Delivery</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="rounded-xl border p-3" style={{ borderColor: '#E8ECEF' }}>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><FaMapMarkerAlt size={10} /> Pickup</p>
            <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{job.origin || '—'}</p>
            <p className="text-xs mt-0.5" style={{ color: '#4A5568' }}>{fmtDate(load.scheduled_pickup_at || load.scheduled_date)}</p>
          </div>
          <div className="rounded-xl border p-3" style={{ borderColor: '#E8ECEF' }}>
            <p className="text-[11px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><FaMapMarkerAlt size={10} /> Delivery</p>
            <p className="text-sm font-medium" style={{ color: '#0A1929' }}>{job.destination || '—'}</p>
            <p className="text-xs mt-0.5" style={{ color: '#4A5568' }}>{fmtDate(job.estimated_end_date)}</p>
          </div>
        </div>

        {job.job_number && (
          <Link to={`/client-job-details/${load.job_id}`} className="inline-flex items-center gap-2 text-xs mt-4 hover:underline" style={{ color: '#2563eb' }}>
            <FaTruck size={11} /> Part of job {job.job_number}
          </Link>
        )}
      </div>

      {/* ── Vehicle + driver ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaTruck style={{ color: '#00A86B' }} /> Vehicle
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">Truck</p>
              <p className="text-sm font-semibold" style={{ color: '#0A1929' }}>{load.truck?.registration_number || 'TBC'}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                <ExpiryChip date={load.truck?.licence_expiry} label="Licence" />
                <ExpiryChip date={load.truck?.cof_expiry} label="CoF" />
              </div>
            </div>
            {[load.trailer_1, load.trailer_2].filter(Boolean).map((t, i) => (
              <div key={t.id || i}>
                <p className="text-[11px] uppercase tracking-wide text-gray-400">Trailer {i + 1}</p>
                <p className="text-sm font-semibold" style={{ color: '#0A1929' }}>{t.registration_number || 'TBC'}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <ExpiryChip date={t.licence_expiry} label="Licence" />
                  <ExpiryChip date={t.cof_expiry} label="CoF" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaUser style={{ color: '#00A86B' }} /> Driver
          </h2>
          {load.driver ? (
            <div className="space-y-2">
              <p className="text-sm font-semibold" style={{ color: '#0A1929' }}>{load.driver.full_name || 'TBC'}</p>
              {load.driver.phone && (
                <a href={`tel:${load.driver.phone}`} className="text-xs flex items-center gap-1 hover:underline" style={{ color: '#2563eb' }}>
                  <FaPhone size={10} /> {load.driver.phone}
                </a>
              )}
              <div className="flex flex-wrap gap-1 pt-1">
                <ExpiryChip date={load.driver.licence_expiry} label="Licence" />
                <ExpiryChip date={load.driver.prdp_expiry} label="PrDP" />
              </div>
              <p className="text-[11px] text-gray-400 flex items-start gap-1 pt-1">
                <FaIdCard size={10} className="mt-0.5" />
                <span>ID / passport copies are held by One Purpose Logistics — ask us if you need to see them.</span>
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No driver assigned yet.</p>
          )}
        </div>
      </div>

      {/* ── Live tracking ── */}
      {(trackingHref || load.tracking_username) && (
        <div className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-bold mb-2 flex items-center gap-2" style={{ color: '#0A1929' }}>
            <FaExternalLinkAlt size={12} style={{ color: '#00A86B' }} /> Live tracking
          </h2>
          {trackingHref ? (
            <a href={trackingHref} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline break-all" style={{ color: '#2563eb' }}>
              {load.tracking_link}
            </a>
          ) : (
            <p className="text-sm text-gray-400">A tracking link has not been shared yet.</p>
          )}
          {(load.tracking_username || load.tracking_password) && (
            <p className="text-xs mt-2" style={{ color: '#4A5568' }}>
              {load.tracking_username && <>Login: <strong>{load.tracking_username}</strong></>}
              {load.tracking_username && load.tracking_password ? ' · ' : ''}
              {load.tracking_password && <>Password: <strong>{load.tracking_password}</strong></>}
            </p>
          )}
          {/* Provider tracking links lapse (MiX's share link lasts about 7 days), so say when this
              one dies - and say so in red once it has, rather than letting the client click a dead
              link and wonder. */}
          {load.tracking_expires_at && (
            <p className="text-xs mt-1" style={{ color: new Date(load.tracking_expires_at) < new Date() ? '#dc2626' : '#4A5568' }}>
              {new Date(load.tracking_expires_at) < new Date() ? 'This tracking link expired on ' : 'Tracking link valid until '}
              <strong>{new Date(load.tracking_expires_at).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
            </p>
          )}
        </div>
      )}

      {/* ── Documents on this load ── */}
      <div className="mb-4">
        <LoadDocumentsPanel loadId={load.id} heading="Documents on this load" />
      </div>

      {/* ── Weights & documents ── */}
      <div className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
        <h2 className="font-bold mb-3 flex items-center gap-2" style={{ color: '#0A1929' }}>
          <FaFileAlt size={12} style={{ color: '#00A86B' }} /> Weights &amp; documents
        </h2>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm" style={{ color: '#4A5568' }}>
          <span>Weighbridge: <strong style={{ color: '#0A1929' }}>{load.weighbridge_weight_mt || '—'} MT</strong></span>
          {load.weighbridge_ticket_number && (
            <span>Ticket: <strong style={{ color: '#0A1929' }}>{load.weighbridge_ticket_number}</strong></span>
          )}
          <span>POD: <strong style={{ color: load.pod_status === 'approved' ? '#16a34a' : '#0A1929' }}>{load.pod_status || 'pending'}</strong></span>
          {load.pod_weight_mt && <span>POD weight: <strong style={{ color: '#0A1929' }}>{load.pod_weight_mt} MT</strong></span>}
        </div>
        <div className="flex flex-wrap gap-3 mt-3">
          {load.weighbridge_image_url && (
            <a href={load.weighbridge_image_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50" style={{ borderColor: '#E8ECEF', color: '#0A1929' }}>
              <FaImage size={11} /> View weighbridge ticket
            </a>
          )}
          {load.pod_image_url && (
            <a href={load.pod_image_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50" style={{ borderColor: '#E8ECEF', color: '#0A1929' }}>
              <FaFileAlt size={11} /> View POD
            </a>
          )}
        </div>
        {load.delay_reason && (
          <p className="text-xs mt-3 flex items-start gap-1" style={{ color: '#b45309' }}>
            <FaClock size={11} className="mt-0.5" /> <span>{load.delay_reason}</span>
          </p>
        )}
      </div>

      {/* ── Timeline ── */}
      {stages.length > 0 && (
        <div className="bg-white rounded-2xl border p-5 mb-4" style={{ borderColor: '#E8ECEF' }}>
          <h2 className="font-bold mb-3" style={{ color: '#0A1929' }}>Progress</h2>
          <div className="space-y-2">
            {stages.map((s) => (
              <div key={s.label} className="flex items-center gap-3 text-sm">
                <FaCheckCircle size={12} style={{ color: '#16a34a' }} />
                <span className="w-24 shrink-0" style={{ color: '#4A5568' }}>{s.label}</span>
                <span style={{ color: '#0A1929' }}>{fmtDateTime(s.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Route map: the route EXACTLY as quoted (stored at quote time) ── */}
      <div className="rounded-2xl border p-5" style={{ borderColor: '#E8ECEF', backgroundColor: '#F8F9FA' }}>
        <h2 className="font-bold mb-1 flex items-center gap-2" style={{ color: '#0A1929' }}>
          <FaMapMarkerAlt size={12} style={{ color: '#00A86B' }} /> Route map
        </h2>
        <p className="text-xs mb-3" style={{ color: '#4A5568' }}>
          This is the exact route your quotation was priced on, saved when the quote was made
          ({job.origin || '—'} → {job.destination || '—'}).
        </p>
        <StoredRouteMap
          route={load.route?.route_optimized || null}
          height={340}
          emptyMessage="This job's route was not captured on the quotation, so there is no stored route to draw yet."
        />
      </div>
    </ClientLayout>
  );
}

export default ClientLoadDetail;


